import React, { useState, useEffect } from "react";
import {
  IconButton,
  Tooltip,
  CircularProgress,
  Box,
  Typography,
  Divider,
  Avatar,
  Link,
} from "@mui/material";
import {
  Delete,
  Email,
  LockReset,
  Block,
  PersonAdd,
  CloudUpload,
} from "@mui/icons-material";
import { auth, db } from "../../firebase-config";
import {
  getAuth,
  sendPasswordResetEmail,
  reauthenticateWithCredential,
  EmailAuthProvider,
  updateProfile,
} from "firebase/auth";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
} from "firebase/firestore";
import Sidebar from "../../components/ui/Sidebar";
import PageHeader from "../../components/ui/PageHeader";
import ButtonComponent from "../../components/ui/ButtonComponent";
import TableComponent from "../../components/ui/TableComponent";
import ModalComponent from "../../components/ui/ModalComponent";
import TextInputComponent from "../../components/ui/TextInputComponent";
import SelectInputComponent from "../../components/ui/SelectInputComponent";
import PasswordInputComponent from "../../components/ui/PasswordInputComponent";
import { useToast } from "../../components/ui/ToastContext";
import "../../style.css";

const ManageUsers = () => {
  const { addToast } = useToast();
  const [users, setUsers] = useState([]);
  const [openCreateUser, setOpenCreateUser] = useState(false);
  const [newUser, setNewUser] = useState({
    displayName: "",
    email: "",
    password: "",
    roles: [],
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showReAuthDialog, setShowReAuthDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [reAuthPassword, setReAuthPassword] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [loadingActions, setLoadingActions] = useState({});

  const orgId = sessionStorage.getItem("organizationId");

  const logSystemError = async (action, message) => {
    try {
      await addDoc(collection(db, "systemLogs"), {
        type: "Error",
        message,
        email: auth.currentUser?.email || "Unknown",
        organizationId: orgId,
        severity: "high",
        timestamp: new Date(),
      });
    } catch (error) {
      console.error("Failed to log system error:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      if (!auth.currentUser) {
        console.warn("User not authenticated. Waiting for auth state...");
        return;
      }

      const idToken = await auth.currentUser.getIdToken(true);

      const response = await fetch(
        "https://getfirebaseusers-774073159618.us-central1.run.app",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({}),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to fetch users.");
      }

      const filteredUsers = data.filter(
        (user) => user.organizationId === orgId
      );

      const formattedUsers = filteredUsers.map((user) => {
        const isDisabled = Boolean(user.disabled);

        // Normalize roles to array form
        const userRoles = Array.isArray(user.roles)
          ? user.roles
          : user.roles
          ? [user.roles]
          : ["user"];

        const primaryRole = userRoles[0] || "user";

        return {
          id: user.uid,
          displayName: user.displayName || "N/A",
          email: user.email,
          roles: userRoles,
          primaryRole,
          status: isDisabled ? "Disabled" : "Active",
          lastLogin: user.lastLogin || "N/A",
          createdDate: user.createdDate || "N/A",
          phone: user.phone || null,
          altEmail: user.altEmail || null,
          emailVerified: user.emailVerified ?? false,
          photoURL: user.photo || null,
          disabled: isDisabled,
        };
      });

      console.log("✅ Formatted Users:", formattedUsers);
      setUsers(formattedUsers);
    } catch (error) {
      console.error("❌ Error fetching users:", error);
      addToast("Failed to fetch users", "error");
      logSystemError("Fetch Users", error.message);
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        fetchUsers();
      } else {
        console.warn("🔴 User is not signed in. FetchUsers() skipped.");
      }
    });

    return () => unsubscribe();
  }, []);

  const handleCreateUser = async () => {
    setIsCreatingUser(true);

    try {
      const idToken = await auth.currentUser.getIdToken(true);

      const response = await fetch(
        "https://us-central1-integrity-resolutions-inc.cloudfunctions.net/createUsers",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
            "organization-id": orgId,
          },
          body: JSON.stringify({
            displayName: newUser.displayName,
            email: newUser.email,
            password: newUser.password,
            roles: newUser.roles,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to create user.");
      }

      console.log("User created successfully:", data);

      await fetch(
        "https://sendemailverification-774073159618.us-central1.run.app",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ email: newUser.email }),
        }
      );

      addToast(
        "User created successfully, and verification email has been sent!"
      );
      setOpenCreateUser(false);
      fetchUsers();
      setNewUser({ email: "", password: "", roles: [], displayName: "" });
    } catch (error) {
      console.error("Error creating user:", error);
      addToast(error.message);
    } finally {
      setIsCreatingUser(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser?.id) {
      addToast("Error: No user selected for deletion.");
      return;
    }

    setLoadingActions((prev) => ({ ...prev, [selectedUser.id]: "delete" }));

    try {
      const idToken = await auth.currentUser.getIdToken(true);
      const response = await fetch(
        "https://us-central1-integrity-resolutions-inc.cloudfunctions.net/deleteUser",
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
            "organization-id": orgId,
          },
          body: JSON.stringify({ userId: selectedUser.id }),
        }
      );

      if (!response.ok) throw new Error("Failed to delete user.");
      addToast("User deleted successfully.");
      fetchUsers();
      setShowDeleteDialog(false);
    } catch (error) {
      console.error("Error deleting user:", error);
      addToast(error.message);
    } finally {
      setLoadingActions((prev) => ({ ...prev, [selectedUser.id]: null }));
    }
  };

  const handleToggleUserStatus = async (userId, disabled) => {
    setLoadingActions((prev) => ({ ...prev, [userId]: "disable" }));

    try {
      const idToken = await auth.currentUser.getIdToken(true);
      const response = await fetch(
        "https://us-central1-integrity-resolutions-inc.cloudfunctions.net/updateUser",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
            "organization-id": orgId,
          },
          body: JSON.stringify({ userId, disabled }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to update user.");
      }

      addToast(`User ${disabled ? "disabled" : "enabled"} successfully.`);

      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId ? { ...user, disabled } : user
        )
      );
    } catch (error) {
      console.error("Error updating user:", error);
      addToast(error.message);
    } finally {
      setLoadingActions((prev) => ({ ...prev, [userId]: null }));
    }
  };

  const handleResetPassword = async (userEmail, userId) => {
    setLoadingActions((prev) => ({ ...prev, [userId]: "reset" }));

    try {
      await sendPasswordResetEmail(auth, userEmail);
      addToast("Password reset email sent.");
    } catch (error) {
      console.error("Error sending password reset:", error);
      addToast(error.message);
    } finally {
      setLoadingActions((prev) => ({ ...prev, [userId]: null }));
    }
  };

  const handleResendVerification = async (userEmail, userId) => {
    setLoadingActions((prev) => ({ ...prev, [userId]: "verify" }));

    try {
      const idToken = await auth.currentUser.getIdToken(true);

      const response = await fetch(
        "https://sendemailverification-774073159618.us-central1.run.app",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ email: userEmail }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error || "Failed to send verification email."
        );
      }

      addToast("Verification email sent successfully.");
    } catch (error) {
      console.error("Error resending verification email:", error);
      addToast(error.message);
    } finally {
      setLoadingActions((prev) => ({ ...prev, [userId]: null }));
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(
        "https://us-central1-integrity-resolutions-inc.cloudfunctions.net/importUsers",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${auth.currentUser?.accessToken}`,
          },
          body: formData,
        }
      );

      const data = await response.json();
      if (response.ok) {
        addToast("Users imported successfully!");
        fetchUsers();
      } else {
        addToast(`Error importing users:  ${data.error}`);
      }
    } catch (error) {
      console.error("Error importing users:", error);
      addToast("Failed to import users.");
    } finally {
      setLoading(false);
    }
  };

  const handleReAuthenticate = async () => {
    try {
      const currentUser = getAuth().currentUser;
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        reAuthPassword
      );
      await reauthenticateWithCredential(currentUser, credential);
      setShowReAuthDialog(false);
      setShowDeleteDialog(true);
    } catch (error) {
      addToast("Re-authentication failed. Please try again.");
      console.error("Re-authentication error:", error);
    }
  };

  const handleOpenCreateUserDialog = () => setOpenCreateUser(true);

  const handleCloseCreateUserDialog = () => {
    setNewUser({ displayName: "", email: "", password: "", roles: [] });
    setOpenCreateUser(false);
  };

  const handleOpenDeleteDialog = (user) => {
    setSelectedUser(user);
    setShowReAuthDialog(true);
  };

  // 🔽 Include VOLUNTEER in the roles list
  const editOptions = {
    email: true,
    roles: [
      { value: "user", label: "User" },
      { value: "admin", label: "Admin" },
      { value: "manager", label: "Manager" },
      { value: "cereset", label: "Cereset User" },
      { value: "volunteer", label: "Volunteer" },
    ],
  };

  const handleCellUpdate = async (rowId, columnId, newValue) => {
    setLoadingActions((prev) => ({ ...prev, [rowId]: true }));

    try {
      const userToUpdate = users.find((user) => user.id === rowId);
      if (!userToUpdate) {
        addToast("Error: User not found.", "error");
        setLoadingActions((prev) => ({ ...prev, [rowId]: false }));
        return;
      }

      const updatedValue =
        columnId === "roles"
          ? Array.isArray(newValue)
            ? newValue
            : [newValue]
          : newValue;

      const updateData = {
        userId: rowId,
        displayName: userToUpdate.displayName,
        email: userToUpdate.email,
        disabled: userToUpdate.disabled,
        roles:
          columnId === "roles" ? updatedValue : userToUpdate.roles,
        [columnId]: updatedValue,
      };

      console.log(`🚀 Sending API Request with Payload:`, updateData);

      const idToken = await auth.currentUser.getIdToken(true);
      const response = await fetch(
        "https://us-central1-integrity-resolutions-inc.cloudfunctions.net/updateUser",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
            "organization-id": orgId,
          },
          body: JSON.stringify(updateData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update user.");
      }

      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === rowId
            ? { ...user, [columnId]: updatedValue }
            : user
        )
      );

      addToast("User updated successfully!", "success");
    } catch (error) {
      console.error("❌ API Call ERROR:", error);
      addToast(error.message, "error");
    } finally {
      setLoadingActions((prev) => ({ ...prev, [rowId]: false }));
    }
  };

  const handleColumnReorder = (newOrder) => {
    console.log("New Column Order:", newOrder);
  };

  const renderExpandedContent = (row) => {
    const formatDate = (dateString) => {
      if (!dateString || dateString === "N/A") return "N/A";

      try {
        const date = new Date(dateString);
        return isNaN(date.getTime())
          ? "Invalid Date"
          : date.toLocaleString();
      } catch {
        return "Error Formatting Date";
      }
    };

    return (
      <Box
        sx={{
          backgroundColor: "#fafafa",
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          padding: 2,
          margin: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          {row.photoURL && (
            <Avatar
              alt={row.displayName}
              src={row.photoURL}
              sx={{ width: 64, height: 64, mr: 2 }}
            />
          )}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              User Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {row.displayName}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="body2" fontWeight="bold">
              User ID:
            </Typography>
            <Typography variant="body2">{row.id}</Typography>
          </Box>

          <Box>
            <Typography variant="body2" fontWeight="bold">
              Email Verified:
            </Typography>
            <Typography variant="body2">
              {row.emailVerified ? "Yes" : "No"}
            </Typography>
          </Box>

          <Box>
            <Typography variant="body2" fontWeight="bold">
              Last Login:
            </Typography>
            <Typography variant="body2">
              {formatDate(row.lastLogin)}
            </Typography>
          </Box>

          <Box>
            <Typography variant="body2" fontWeight="bold">
              Created Date:
            </Typography>
            <Typography variant="body2">
              {formatDate(row.createdDate)}
            </Typography>
          </Box>

          <Box>
            <Typography variant="body2" fontWeight="bold">
              Phone:
            </Typography>
            {row.phone ? (
              <Link href={`tel:${row.phone}`} underline="hover">
                {row.phone}
              </Link>
            ) : (
              <Typography variant="body2">N/A</Typography>
            )}
          </Box>

          <Box>
            <Typography variant="body2" fontWeight="bold">
              Alternate Email:
            </Typography>
            {row.altEmail ? (
              <Link href={`mailto:${row.altEmail}`} underline="hover">
                {row.altEmail}
              </Link>
            ) : (
              <Typography variant="body2">N/A</Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <div className="manage-users-container">
      <Sidebar />
      <div className="manage-users-content">
        <PageHeader
          title="Manage Users"
          showSearch={true}
          onSearch={(query) => setSearchTerm(query)}
          actions={
            <>
              <ButtonComponent
                label="Create User"
                type="primary"
                icon={<PersonAdd />}
                onClick={handleOpenCreateUserDialog}
              />
              <ButtonComponent
                label="Import Users"
                type="secondary"
                icon={<CloudUpload />}
                isLoading={loading}
                onClick={() =>
                  document.getElementById("importFileInput").click()
                }
              />
              <input
                id="importFileInput"
                type="file"
                hidden
                onChange={handleFileUpload}
              />
            </>
          }
        />

        <TableComponent
          columns={[
            { id: "displayName", label: "Name" },
            {
              id: "email",
              label: "Email",
              editable: true,
              editorType: "text",
            },
            {
              id: "roles",
              label: "Roles",
              editable: true,
              editorType: "multi-select",
            },
            { id: "status", label: "Status" },
            { id: "actions", label: "Actions", isActionColumn: true },
          ]}
          data={users
            .filter(
              (user) =>
                user.email
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase()) ||
                user.displayName
                  ?.toLowerCase()
                  .includes(searchTerm.toLowerCase())
            )
            .map((user) => ({
              ...user,
              actions: (
                <>
                  <Tooltip title="Disable User">
                    <IconButton
                      onClick={() =>
                        handleToggleUserStatus(user.id, !user.disabled)
                      }
                      color="warning"
                      disabled={
                        loadingActions[user.id] === "disable"
                      }
                    >
                      {loadingActions[user.id] === "disable" ? (
                        <CircularProgress size={20} />
                      ) : (
                        <Block />
                      )}
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Delete User">
                    <IconButton
                      onClick={() => handleOpenDeleteDialog(user)}
                      color="error"
                      disabled={
                        loadingActions[user.id] === "delete"
                      }
                    >
                      {loadingActions[user.id] === "delete" ? (
                        <CircularProgress size={20} />
                      ) : (
                        <Delete />
                      )}
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Send Password Reset">
                    <IconButton
                      onClick={() =>
                        handleResetPassword(user.email, user.id)
                      }
                      color="info"
                      disabled={
                        loadingActions[user.id] === "reset"
                      }
                    >
                      {loadingActions[user.id] === "reset" ? (
                        <CircularProgress size={20} />
                      ) : (
                        <LockReset />
                      )}
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Resend Verification Email">
                    <IconButton
                      onClick={() =>
                        handleResendVerification(user.email, user.id)
                      }
                      color="primary"
                      disabled={
                        loadingActions[user.id] === "verify"
                      }
                    >
                      {loadingActions[user.id] === "verify" ? (
                        <CircularProgress size={20} />
                      ) : (
                        <Email />
                      )}
                    </IconButton>
                  </Tooltip>
                </>
              ),
            }))}
          sortable={true}
          paginated={true}
          editOptions={editOptions}
          onCellUpdate={handleCellUpdate}
          onColumnReorder={handleColumnReorder}
          persistKey="manageUsersTable"
          initialSortBy="displayName"
          initialSortDirection="asc"
          groupBy="primaryRole"
          renderExpandedContent={renderExpandedContent}
        />

        <ModalComponent
          open={showReAuthDialog}
          title="Re-Authenticate to Delete"
          content={
            <div style={{ paddingTop: "10px" }}>
              <p>To delete this user, please re-enter your password.</p>
              <PasswordInputComponent
                label="Password"
                value={reAuthPassword}
                onChange={(e) => setReAuthPassword(e.target.value)}
                required
              />
            </div>
          }
          confirmLabel="Continue"
          cancelLabel="Cancel"
          onConfirm={handleReAuthenticate}
          onCancel={() => setShowReAuthDialog(false)}
        />

        <ModalComponent
          open={openCreateUser}
          title="Create User"
          content={
            <>
              <TextInputComponent
                label="Display Name"
                value={newUser.displayName}
                onChange={(e) =>
                  setNewUser({
                    ...newUser,
                    displayName: e.target.value,
                  })
                }
                required
              />
              <TextInputComponent
                label="Email"
                type="email"
                value={newUser.email}
                onChange={(e) =>
                  setNewUser({
                    ...newUser,
                    email: e.target.value,
                  })
                }
                required
              />
              <PasswordInputComponent
                label="Password"
                value={newUser.password}
                onChange={(e) =>
                  setNewUser({
                    ...newUser,
                    password: e.target.value,
                  })
                }
                required
              />
              <SelectInputComponent
                label="Roles"
                value={newUser.roles}
                onChange={(e) => {
                  const selected = Array.isArray(e.target.value)
                    ? e.target.value
                    : [e.target.value];
                  setNewUser({ ...newUser, roles: selected });
                }}
                options={[
                  { value: "user", label: "User" },
                  { value: "admin", label: "Admin" },
                  { value: "manager", label: "Manager" },
                  { value: "cereset", label: "Cereset User" },
                  { value: "volunteer", label: "Volunteer" },
                ]}
                multiple={true}
                required
              />
            </>
          }
          confirmLabel="Create"
          cancelLabel="Cancel"
          isLoading={isCreatingUser}
          onConfirm={handleCreateUser}
          onCancel={handleCloseCreateUserDialog}
        />

        <ModalComponent
          open={showDeleteDialog}
          title="Delete User?"
          content={`Are you sure you want to delete ${
            selectedUser?.displayName || selectedUser?.email
          }? This action cannot be undone.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          isLoading={
            loadingActions[selectedUser?.id] === "delete"
          }
          onConfirm={handleDeleteUser}
          onCancel={() => setShowDeleteDialog(false)}
        />
      </div>
    </div>
  );
};

export default ManageUsers;